import React from 'react';
import { usePortalContext } from '../context/PortalContext';
import AdminTenantSelector, { type AdminTenantOption } from '../components/AdminTenantSelector';
import TranslationPolicyCard from './TranslationPolicyCard';

type AiFieldCatalogItem = {
  fieldKey: string;
  listKey: string;
  internalName: string;
  displayName: string;
  description?: string;
  compatibility?: {
    availableOnInstances: number;
    reportingInstances: number;
    availability: 'all_reporting_instances' | 'some_reporting_instances';
  };
};

type AiTextEnhancementConfig = {
  enabled: boolean;
  mandatoryAnonymisation: boolean;
  allowedFields: string[];
  instanceMode: 'all_instances' | 'selected_instances';
  allowedInstanceIds: string[];
  includedPreviewRequests: number;
  allowOverage: boolean;
  styleProfile: 'professional_neutral';
  instructionProfile: {
    writingStyle: 'professional_neutral' | 'formal_compliance' | 'concise_operational';
    formattingPreference: 'preserve_structure' | 'short_paragraphs' | 'preserve_lists';
    outputStructure: 'plain_paragraphs' | 'plain_paragraphs_and_lists';
    tenantGuidance?: string;
  };
};

type AiDataPathMode =
  | 'edinshall_managed_inference'
  | 'byo_endpoint_via_edinshall'
  | 'customer_tenant_inference';

type AiDataPathDisclosure = {
  mode: AiDataPathMode;
  summary: string;
  redactBeforePrompt: boolean;
};

type AiActivityEvent = {
  eventId: string;
  eventTimestamp: string;
  eventType: 'preview' | 'accept' | 'discard' | 'error';
  fieldKey: string;
  modeApplied: string | null;
  correlationId: string;
  actorObjectId: string | null;
  instanceId: string;
  originalLength: number | null;
  suggestedLength: number | null;
  acceptedLength: number | null;
  redactionCount: number | null;
  providerModel: string | null;
  errorCode: string | null;
};

type UsageSummary = {
  billingPeriodId: string;
  usedThisPeriod: number;
  includedPreviewRequests: number;
  remainingCredits: number;
  allowOverage: boolean;
};

type InstanceItem = {
  instanceId: string;
  siteUrl: string;
  status: string;
};

const defaultConfig: AiTextEnhancementConfig = {
  enabled: false,
  mandatoryAnonymisation: false,
  allowedFields: [],
  instanceMode: 'all_instances',
  allowedInstanceIds: [],
  includedPreviewRequests: 25,
  allowOverage: false,
  styleProfile: 'professional_neutral',
  instructionProfile: {
    writingStyle: 'professional_neutral',
    formattingPreference: 'preserve_structure',
    outputStructure: 'plain_paragraphs',
    tenantGuidance: ''
  }
};

const defaultAvailableFields: AiFieldCatalogItem[] = [];

const formatCompatibility = (field: AiFieldCatalogItem): string | null => {
  const compatibility = field.compatibility;
  if (!compatibility || compatibility.reportingInstances <= 0) {
    return null;
  }
  if (compatibility.availability === 'all_reporting_instances') {
    return `Available on all ${compatibility.reportingInstances} reporting installation${compatibility.reportingInstances === 1 ? '' : 's'}.`;
  }
  return `Available on ${compatibility.availableOnInstances} of ${compatibility.reportingInstances} reporting installations. Older installations may need an SPFx upgrade.`;
};

const AiTextEnhancementAdmin = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const sessionTenantId = String(tenant?.tenantId ?? '').trim().toLowerCase();
  const hasAccess = tenant?.capabilities?.canReviewAiUsage === true;
  const canEdit = tenant?.capabilities?.canManageAiPolicy === true;
  const [config, setConfig] = React.useState<AiTextEnhancementConfig>(defaultConfig);
  const [usage, setUsage] = React.useState<UsageSummary | null>(null);
  const [instances, setInstances] = React.useState<InstanceItem[]>([]);
  const [availableFields, setAvailableFields] = React.useState<AiFieldCatalogItem[]>(defaultAvailableFields);
  const [deployableTenants, setDeployableTenants] = React.useState<AdminTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savedMessage, setSavedMessage] = React.useState<string | null>(null);
  const [dataPath, setDataPath] = React.useState<AiDataPathDisclosure | null>(null);
  // Portal-controlled per-installation off-switch state. Loaded
  // alongside config so the toggle list reflects current truth.
  type OffSwitchState = { instanceId: string; disabled: boolean; disabledAt: string | null; disabledBy: string | null };
  const [offSwitchStates, setOffSwitchStates] = React.useState<OffSwitchState[]>([]);
  const [offSwitchPending, setOffSwitchPending] = React.useState<Set<string>>(new Set());
  const [offSwitchError, setOffSwitchError] = React.useState<string | null>(null);
  // Activity ledger state. Loaded lazily on demand so the page open is
  // fast even for tenants with thousands of events.
  const [activityEvents, setActivityEvents] = React.useState<AiActivityEvent[]>([]);
  const [activityLoading, setActivityLoading] = React.useState(false);
  const [activityError, setActivityError] = React.useState<string | null>(null);
  const [activityLoaded, setActivityLoaded] = React.useState(false);
  const [activityFilter, setActivityFilter] = React.useState<{
    from: string;
    to: string;
    fieldKey: string;
    eventType: '' | 'preview' | 'accept' | 'discard' | 'error';
  }>({ from: '', to: '', fieldKey: '', eventType: '' });

  const load = React.useCallback(async (): Promise<void> => {
    if (!hasAccess) {
      return;
    }
    if (!selectedTenantId) {
      return;
    }
    setLoading(true);
    setError(null);
    setSavedMessage(null);
    try {
      const [configResponse, usageResponse, instancesResponse, offSwitchResponse] = await Promise.all([
        fetch(`/api/admin/ai-text-enhancement?tenantId=${encodeURIComponent(selectedTenantId)}`),
        fetch(`/api/admin/ai-text-enhancement/usage-summary?tenantId=${encodeURIComponent(selectedTenantId)}`),
        // Tenant-scoped — must match the off-switch fetch below so the
        // instance rows and the off-switch states are from the same
        // tenant context (Codex finding on PR #139).
        fetch(`/api/instances?tenantId=${encodeURIComponent(selectedTenantId)}`),
        fetch(`/api/admin/ai-text-enhancement/installations?tenantId=${encodeURIComponent(selectedTenantId)}`)
      ]);

      const configPayload = await configResponse.json().catch(() => ({}));
      const usagePayload = await usageResponse.json().catch(() => ({}));
      const instancesPayload = await instancesResponse.json().catch(() => ({}));
      const offSwitchPayload = await offSwitchResponse.json().catch(() => ({}));

      if (!configResponse.ok) {
        throw new Error(configPayload?.message || configPayload?.error || `Failed to load AI config (${configResponse.status})`);
      }
      if (!usageResponse.ok) {
        throw new Error(usagePayload?.message || usagePayload?.error || `Failed to load AI usage (${usageResponse.status})`);
      }
      if (!instancesResponse.ok) {
        throw new Error(instancesPayload?.message || instancesPayload?.error || `Failed to load instances (${instancesResponse.status})`);
      }

      setConfig({
        ...defaultConfig,
        ...(configPayload?.config ?? {})
      });
      setAvailableFields(
        Array.isArray(configPayload?.availableFields)
          ? configPayload.availableFields as AiFieldCatalogItem[]
          : defaultAvailableFields
      );
      setDataPath((configPayload?.dataPath ?? null) as AiDataPathDisclosure | null);
      setUsage((usagePayload?.usage ?? null) as UsageSummary | null);
      setInstances(Array.isArray(instancesPayload?.instances) ? instancesPayload.instances as InstanceItem[] : []);
      setOffSwitchStates(
        offSwitchResponse.ok && Array.isArray(offSwitchPayload?.installations)
          ? (offSwitchPayload.installations as OffSwitchState[])
          : []
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [hasAccess, selectedTenantId]);

  React.useEffect(() => {
    const loadTenants = async (): Promise<void> => {
      if (!hasAccess) {
        return;
      }
      try {
        const response = await fetch('/api/admin/spfx-releases/deployable-tenants');
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.message || `Failed to load tenants (${response.status})`);
        }
        const tenants = Array.isArray(payload?.tenants) ? payload.tenants as AdminTenantOption[] : [];
        setDeployableTenants(tenants);
        setSelectedTenantId(payload?.defaultTenantId || tenants[0]?.tenantId || sessionTenantId);
      } catch (err) {
        setError((err as Error).message);
      }
    };
    void loadTenants();
  }, [hasAccess, sessionTenantId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const toggleInstance = (instanceId: string): void => {
    setConfig((prev) => {
      const exists = prev.allowedInstanceIds.includes(instanceId);
      return {
        ...prev,
        allowedInstanceIds: exists
          ? prev.allowedInstanceIds.filter((id) => id !== instanceId)
          : [...prev.allowedInstanceIds, instanceId]
      };
    });
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      if (!canEdit) {
        throw new Error('You do not have permission to change AI text settings.');
      }
      const response = await fetch('/api/admin/ai-text-enhancement', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId, config })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || payload?.error || `Failed to save AI config (${response.status})`);
      }
      setSavedMessage('AI text enhancement settings saved.');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Builds the activity-events query string with the page's selected
  // tenant baked in, plus the user-set filters. The portal route is at
  // /api/admin/ai-text-enhancement/activity-events (selected-tenant
  // scoped via ?tenantId=); the platform API does the actual lookup
  // against its admin-tenant endpoint.
  const buildActivityQueryString = (extra?: Record<string, string>): string => {
    const params = new URLSearchParams();
    if (selectedTenantId) { params.set('tenantId', selectedTenantId); }
    if (activityFilter.from) { params.set('from', activityFilter.from); }
    if (activityFilter.to) { params.set('to', activityFilter.to); }
    if (activityFilter.fieldKey) { params.set('fieldKey', activityFilter.fieldKey); }
    if (activityFilter.eventType) { params.set('eventType', activityFilter.eventType); }
    params.set('limit', '500');
    if (extra) {
      for (const [key, value] of Object.entries(extra)) {
        params.set(key, value);
      }
    }
    return params.toString();
  };

  const loadActivityEvents = async (): Promise<void> => {
    if (!selectedTenantId) {
      setActivityError('Select a tenant before loading activity events.');
      return;
    }
    setActivityLoading(true);
    setActivityError(null);
    try {
      const response = await fetch(`/api/admin/ai-text-enhancement/activity-events?${buildActivityQueryString()}`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || payload?.error || `Failed to load activity events (${response.status})`);
      }
      setActivityEvents(Array.isArray(payload?.events) ? payload.events : []);
      setActivityLoaded(true);
    } catch (err) {
      setActivityError((err as Error).message);
    } finally {
      setActivityLoading(false);
    }
  };

  const downloadActivityCsv = (): void => {
    if (!selectedTenantId) {
      setActivityError('Select a tenant before downloading the CSV.');
      return;
    }
    const params = buildActivityQueryString({ format: 'csv' });
    window.open(`/api/admin/ai-text-enhancement/activity-events?${params}`, '_blank');
  };

  const toggleInstallationOffSwitch = async (instanceId: string, nextDisabled: boolean): Promise<void> => {
    setOffSwitchError(null);
    setOffSwitchPending((prev) => {
      const next = new Set(prev);
      next.add(instanceId);
      return next;
    });
    try {
      const response = await fetch(`/api/admin/ai-text-enhancement/installations/${encodeURIComponent(instanceId)}/disabled`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId, disabled: nextDisabled })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || payload?.error || `Failed to update off-switch (${response.status})`);
      }
      // Optimistic update reflecting what the API returned
      setOffSwitchStates((prev) => {
        const next = prev.slice();
        const idx = next.findIndex((row) => row.instanceId === instanceId);
        const updated: OffSwitchState = payload?.state ?? {
          instanceId,
          disabled: nextDisabled,
          disabledAt: nextDisabled ? new Date().toISOString() : null,
          disabledBy: nextDisabled ? 'you' : null
        };
        if (idx === -1) {
          next.push(updated);
        } else {
          next[idx] = updated;
        }
        return next;
      });
    } catch (err) {
      setOffSwitchError((err as Error).message);
    } finally {
      setOffSwitchPending((prev) => {
        const next = new Set(prev);
        next.delete(instanceId);
        return next;
      });
    }
  };

  if (!hasAccess) {
    return (
      <div className="card">
        <h1 className="section-title">AI Text Enhancement</h1>
        <p className="muted">You do not have permission to manage metered AI settings.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="section-title">AI Text Enhancement</h1>
      <p className="muted">
        Configure the tenant-controlled AI enhancement preview for enabled audit text fields. Personal data removal is governed separately through the PII Detection page.
      </p>

      <div className="card" style={{ marginTop: '12px', borderLeft: '3px solid var(--accent, #5b8def)' }}>
        <h3>Data path</h3>
        <div className="muted" style={{ marginTop: '6px', lineHeight: '1.55' }}>
          {/* Primary line: typed disclosure object the platform API
              returns alongside config. Falls back to the inline summary
              if an older API build doesn't yet return the field. */}
          <strong>Where your content goes:</strong>{' '}
          {dataPath?.summary ?? 'Edinshall-managed Azure OpenAI hosted in the Edinshall-controlled subscription. Requests are server-side only — the SPFx installation never speaks to the model directly.'}
          <br />
          <strong>Inference path:</strong>{' '}
          {dataPath?.mode === 'byo_endpoint_via_edinshall'
            ? 'Customer-supplied Azure OpenAI endpoint, orchestrated by Edinshall.'
            : dataPath?.mode === 'customer_tenant_inference'
              ? 'Customer-tenant inference (true on-tenant execution).'
              : 'Edinshall-managed Azure OpenAI inference (default).'}
          <br />
          <strong>Pre-prompt redaction:</strong>{' '}
          {dataPath?.redactBeforePrompt === false
            ? 'Not applied on this path.'
            : 'Source text is run through Azure AI Language to scrub personal data before any prompt is sent. Always on; not user-selectable.'}
          <br />
          <strong>Output processing posture:</strong> auditors choose <em>Format only</em> or <em>Thorough anonymise</em> per invocation. Setting <em>Require thorough anonymisation</em> below removes that choice and forces thorough output anonymisation for every request.
          <br />
          <strong>Customer-supplied inference (BYO Azure OpenAI):</strong>{' '}
          {dataPath?.mode === 'byo_endpoint_via_edinshall' ? 'Active for this tenant.' : 'Enterprise-tier add-on. Not active on this tenant.'}
          <br />
          <strong>On-tenant inference:</strong>{' '}
          {dataPath?.mode === 'customer_tenant_inference' ? 'Active for this tenant.' : 'Not available.'}
        </div>
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Activation and credits</h3>
        <div style={{ marginTop: '10px', marginBottom: '12px' }}>
          <AdminTenantSelector
            value={selectedTenantId}
            options={deployableTenants}
            onChange={setSelectedTenantId}
            disabled={saving || loading}
            helperText="AI policy is tenant-scoped. Configure one covered tenant at a time."
          />
        </div>

        <div style={{ display: 'grid', gap: '12px' }}>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(event) => setConfig((prev) => ({ ...prev, enabled: event.target.checked }))}
            />
            <span>Enable AI text enhancement</span>
          </label>

          <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              style={{ marginTop: '4px' }}
              checked={config.mandatoryAnonymisation}
              onChange={(event) => setConfig((prev) => ({ ...prev, mandatoryAnonymisation: event.target.checked }))}
            />
            <span>
              <strong>Require thorough anonymisation</strong>
              <div className="muted" style={{ marginTop: '2px' }}>
                Locks the output mode to <em>Thorough anonymise</em> at item level. Auditors cannot opt down to <em>Format only</em>. Pre-prompt redaction runs either way.
              </div>
            </span>
          </label>

          <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={config.allowOverage}
              onChange={(event) => setConfig((prev) => ({ ...prev, allowOverage: event.target.checked }))}
            />
            <span>Allow usage beyond included preview credits</span>
          </label>

          <div className="grid columns-2">
            <label>
              <div className="muted" style={{ marginBottom: '4px' }}>Included preview credits per billing period</div>
              <input
                className="input"
                type="number"
                min={0}
                value={config.includedPreviewRequests}
                onChange={(event) => setConfig((prev) => ({
                  ...prev,
                  includedPreviewRequests: Math.max(0, Number(event.target.value || 0))
                }))}
              />
            </label>
          </div>

          <div className="card" style={{ marginTop: '8px' }}>
            <strong>Instruction profile</strong>
            <div className="muted" style={{ marginTop: '6px', marginBottom: '10px' }}>
              These bounded instructions apply across enabled installations for this tenant. They guide formatting and tone without allowing raw prompt editing.
            </div>

            <div className="grid columns-2">
              <label>
                <div className="muted" style={{ marginBottom: '4px' }}>Writing style</div>
                <select
                  className="input"
                  value={config.instructionProfile.writingStyle}
                  onChange={(event) => setConfig((prev) => ({
                    ...prev,
                    instructionProfile: {
                      ...prev.instructionProfile,
                      writingStyle: event.target.value as AiTextEnhancementConfig['instructionProfile']['writingStyle']
                    }
                  }))}
                >
                  <option value="professional_neutral">Professional neutral</option>
                  <option value="formal_compliance">Formal compliance</option>
                  <option value="concise_operational">Concise operational</option>
                </select>
              </label>

              <label>
                <div className="muted" style={{ marginBottom: '4px' }}>Formatting preference</div>
                <select
                  className="input"
                  value={config.instructionProfile.formattingPreference}
                  onChange={(event) => setConfig((prev) => ({
                    ...prev,
                    instructionProfile: {
                      ...prev.instructionProfile,
                      formattingPreference: event.target.value as AiTextEnhancementConfig['instructionProfile']['formattingPreference']
                    }
                  }))}
                >
                  <option value="preserve_structure">Preserve existing structure</option>
                  <option value="short_paragraphs">Split into short paragraphs</option>
                  <option value="preserve_lists">Preserve list structure</option>
                </select>
              </label>
            </div>

            <div className="grid columns-2" style={{ marginTop: '12px' }}>
              <label>
                <div className="muted" style={{ marginBottom: '4px' }}>Output structure</div>
                <select
                  className="input"
                  value={config.instructionProfile.outputStructure}
                  onChange={(event) => setConfig((prev) => ({
                    ...prev,
                    instructionProfile: {
                      ...prev.instructionProfile,
                      outputStructure: event.target.value as AiTextEnhancementConfig['instructionProfile']['outputStructure']
                    }
                  }))}
                >
                  <option value="plain_paragraphs">Plain paragraphs</option>
                  <option value="plain_paragraphs_and_lists">Paragraphs and simple lists</option>
                </select>
              </label>
            </div>

            <label style={{ display: 'block', marginTop: '12px' }}>
              <div className="muted" style={{ marginBottom: '4px' }}>Tenant guidance</div>
              <textarea
                className="input"
                rows={4}
                maxLength={320}
                value={config.instructionProfile.tenantGuidance ?? ''}
                onChange={(event) => setConfig((prev) => ({
                  ...prev,
                  instructionProfile: {
                    ...prev.instructionProfile,
                    tenantGuidance: event.target.value
                  }
                }))}
                placeholder="Example: Use UK English and write in a neutral QA audit style. Preserve meaningful paragraph breaks."
              />
              <div className="muted" style={{ marginTop: '4px' }}>
                Short bounded guidance only. Safety and grounding rules remain platform-controlled.
              </div>
            </label>
          </div>

          <div>
            <div className="muted" style={{ marginBottom: '6px' }}>Enabled input fields</div>
            {availableFields.length === 0 && (
              <div className="badge-subtle" style={{ marginBottom: '10px' }}>
                No schema-derived AI field catalog has been reported by any active installation yet.
              </div>
            )}
            <div style={{ display: 'grid', gap: '10px' }}>
              {availableFields.map((field) => (
                <label key={field.fieldKey} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={config.allowedFields.includes(field.fieldKey)}
                    onChange={(event) => setConfig((prev) => ({
                      ...prev,
                      allowedFields: event.target.checked
                        ? Array.from(new Set([...prev.allowedFields, field.fieldKey]))
                        : prev.allowedFields.filter((entry) => entry !== field.fieldKey)
                    }))}
                  />
                  <span>
                    <strong>{field.displayName}</strong>
                    <br />
                    <span className="muted">{field.fieldKey}</span>
                    {formatCompatibility(field) && (
                      <>
                        <br />
                        <span className="muted">{formatCompatibility(field)}</span>
                      </>
                    )}
                    {field.description && (
                      <>
                        <br />
                        <span className="muted">{field.description}</span>
                      </>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="muted" style={{ marginBottom: '6px' }}>Instance eligibility</div>
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
              <input
                type="radio"
                name="instance-mode"
                checked={config.instanceMode === 'all_instances'}
                onChange={() => setConfig((prev) => ({ ...prev, instanceMode: 'all_instances' }))}
              />
              <span>Allow on all installations</span>
            </label>
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
              <input
                type="radio"
                name="instance-mode"
                checked={config.instanceMode === 'selected_instances'}
                onChange={() => setConfig((prev) => ({ ...prev, instanceMode: 'selected_instances' }))}
              />
              <span>Allow on selected installations only</span>
            </label>

            {config.instanceMode === 'selected_instances' && (
              <div style={{ display: 'grid', gap: '6px', maxHeight: '240px', overflow: 'auto', border: '1px solid var(--border-subtle)', padding: '10px', borderRadius: '10px' }}>
                {instances.length === 0 && <span className="badge-subtle">No installations available.</span>}
                {instances.map((instance) => (
                  <label key={instance.instanceId} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <input
                      type="checkbox"
                      checked={config.allowedInstanceIds.includes(instance.instanceId)}
                      onChange={() => toggleInstance(instance.instanceId)}
                    />
                    <span>
                      <strong>{instance.siteUrl}</strong>
                      <br />
                      <span className="muted">{instance.instanceId} · {instance.status}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button className="btn" type="button" onClick={() => { void save(); }} disabled={saving || loading || !canEdit}>
              {saving ? 'Saving...' : 'Save AI settings'}
            </button>
            <button className="btn secondary" type="button" onClick={() => { void load(); }} disabled={saving || loading}>
              Reload
            </button>
          </div>

          {usage && (
            <div className="card" style={{ marginTop: '8px' }}>
              <strong>Current usage</strong>
              <div className="muted" style={{ marginTop: '6px' }}>
                Billing period: {usage.billingPeriodId}
              </div>
              <div className="muted">Used previews: {usage.usedThisPeriod}</div>
              <div className="muted">Included credits: {usage.includedPreviewRequests}</div>
              <div className="muted">Remaining credits: {usage.remainingCredits}</div>
              <div className="muted">Overage enabled: {usage.allowOverage ? 'Yes' : 'No'}</div>
            </div>
          )}

          {loading && <span className="badge-subtle">Loading AI settings...</span>}
          {error && <span className="pill warning">{error}</span>}
          {!canEdit && <span className="badge-subtle">Read-only AI usage view</span>}
          {savedMessage && <span className="pill success">{savedMessage}</span>}
        </div>
      </div>

      <TranslationPolicyCard selectedTenantId={selectedTenantId} canEdit={canEdit} />

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Per-installation AI off-switch (portal-controlled)</h3>
        <div className="muted" style={{ marginTop: '6px', marginBottom: '12px' }}>
          Central / Finance lever. Use this to switch AI off for specific installations from the portal — for example, when the cost should be borne by the central org and a particular installation has used its allocation. This is a separate axis from the installation-owner's local SP-property kill switch in the SPFx admin surface; AI is off if EITHER switch is set.
        </div>
        {instances.length === 0 ? (
          <div className="muted">No installations reporting for this tenant.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border, #ddd)' }}>
                <th style={{ padding: '6px 8px' }}>Installation</th>
                <th style={{ padding: '6px 8px' }}>Status</th>
                <th style={{ padding: '6px 8px' }}>Off-switch</th>
                <th style={{ padding: '6px 8px' }}>Last toggled</th>
                <th style={{ padding: '6px 8px' }}>By</th>
              </tr>
            </thead>
            <tbody>
              {instances.map((instance) => {
                const state = offSwitchStates.find((s) => s.instanceId === instance.instanceId);
                const disabled = Boolean(state?.disabled);
                const pending = offSwitchPending.has(instance.instanceId);
                return (
                  <tr key={instance.instanceId} style={{ borderBottom: '1px solid var(--border, #eee)' }}>
                    <td style={{ padding: '6px 8px' }}>
                      <div>{instance.siteUrl}</div>
                      <div className="muted" style={{ fontSize: '11px', fontFamily: 'monospace' }}>{instance.instanceId}</div>
                    </td>
                    <td style={{ padding: '6px 8px' }}>{instance.status}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <label style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={disabled}
                          disabled={!canEdit || pending}
                          onChange={(event) => { void toggleInstallationOffSwitch(instance.instanceId, event.target.checked); }}
                        />
                        <span>{disabled ? 'AI off' : 'AI on'}</span>
                      </label>
                    </td>
                    <td style={{ padding: '6px 8px' }} className="muted">
                      {state?.disabledAt ? state.disabledAt.replace('T', ' ').replace(/\.\d+Z$/, 'Z') : '—'}
                    </td>
                    <td style={{ padding: '6px 8px' }} className="muted">{state?.disabledBy ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {offSwitchError && <div className="pill warning" style={{ marginTop: '10px' }}>{offSwitchError}</div>}
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>AI activity ledger</h3>
        <div className="muted" style={{ marginTop: '6px', marginBottom: '12px' }}>
          Per-event record of preview / accept / discard / error activity. Use this as the human-review evidence trail required by ISO 42001, NIST AI RMF, and EU AI Act expectations. Download as CSV for governance attestations.
        </div>

        <div className="grid columns-2" style={{ gap: '10px' }}>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>From (ISO timestamp)</div>
            <input
              className="input"
              type="datetime-local"
              value={activityFilter.from}
              onChange={(event) => setActivityFilter((prev) => ({ ...prev, from: event.target.value }))}
            />
          </label>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>To (ISO timestamp)</div>
            <input
              className="input"
              type="datetime-local"
              value={activityFilter.to}
              onChange={(event) => setActivityFilter((prev) => ({ ...prev, to: event.target.value }))}
            />
          </label>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Field key (e.g. finding.description)</div>
            <input
              className="input"
              type="text"
              value={activityFilter.fieldKey}
              onChange={(event) => setActivityFilter((prev) => ({ ...prev, fieldKey: event.target.value }))}
            />
          </label>
          <label>
            <div className="muted" style={{ marginBottom: '4px' }}>Event type</div>
            <select
              className="input"
              value={activityFilter.eventType}
              onChange={(event) => setActivityFilter((prev) => ({ ...prev, eventType: event.target.value as typeof activityFilter.eventType }))}
            >
              <option value="">All</option>
              <option value="preview">Preview</option>
              <option value="accept">Accept</option>
              <option value="discard">Discard</option>
              <option value="error">Error</option>
            </select>
          </label>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <button className="btn" type="button" onClick={() => { void loadActivityEvents(); }} disabled={activityLoading}>
            {activityLoading ? 'Loading...' : 'Load events'}
          </button>
          <button className="btn secondary" type="button" onClick={downloadActivityCsv}>
            Download CSV
          </button>
        </div>

        {activityError && <div className="pill warning" style={{ marginTop: '10px' }}>{activityError}</div>}

        {activityLoaded && !activityLoading && (
          <div style={{ marginTop: '12px' }}>
            <div className="muted">Showing {activityEvents.length} event{activityEvents.length === 1 ? '' : 's'} (most recent first; capped at 500).</div>
            {activityEvents.length > 0 && (
              <div style={{ marginTop: '8px', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border, #ddd)' }}>
                      <th style={{ padding: '6px 8px' }}>When (UTC)</th>
                      <th style={{ padding: '6px 8px' }}>Type</th>
                      <th style={{ padding: '6px 8px' }}>Field</th>
                      <th style={{ padding: '6px 8px' }}>Mode</th>
                      <th style={{ padding: '6px 8px' }}>Redactions</th>
                      <th style={{ padding: '6px 8px' }}>Actor</th>
                      <th style={{ padding: '6px 8px' }}>Correlation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityEvents.map((event) => (
                      <tr key={event.eventId} style={{ borderBottom: '1px solid var(--border, #eee)' }}>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{event.eventTimestamp.replace('T', ' ').replace(/\.\d+Z$/, 'Z')}</td>
                        <td style={{ padding: '6px 8px' }}>{event.eventType}</td>
                        <td style={{ padding: '6px 8px' }}>{event.fieldKey}</td>
                        <td style={{ padding: '6px 8px' }}>{event.modeApplied ?? '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{event.redactionCount ?? '—'}</td>
                        <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '11px' }}>{event.actorObjectId ?? '—'}</td>
                        <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '11px' }}>{event.correlationId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiTextEnhancementAdmin;


